import React from 'react';
import { Gift, CreditCard, Mail, Phone } from 'lucide-react';

const GiftVouchersPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold text-slate-800 mb-8">Gift Vouchers</h1>
        
        <div className="prose prose-lg max-w-none">
          <p className="text-lg text-gray-700 mb-8">
            Give the gift of mental health and wellbeing with our HealthCity gift vouchers. 
            Perfect for loved ones who could benefit from professional support and counseling services.
          </p>

          {/* Hero Section */}
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-8 mb-12 text-center">
            <Gift className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-slate-800 mb-4">Give the Gift of Wellbeing</h2>
            <p className="text-gray-700 mb-6">
              Our gift vouchers can be used for any of our services, from therapy sessions to assessments and wellness programs.
            </p>
            <button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200">
              Purchase Gift Voucher
            </button>
          </div>

          {/* Voucher Options */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-slate-800 mb-6">Available Gift Vouchers</h2>
            <div className="grid md:grid-cols-3 gap-6">
              
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow duration-200">
                <div className="text-center mb-4">
                  <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl font-bold text-green-600">£</span>
                  </div>
                  <h3 className="text-xl font-semibold text-slate-800">Single Session</h3>
                </div>
                <ul className="space-y-2 text-gray-700 mb-4">
                  <li>• One 50-minute therapy session</li>
                  <li>• Valid for 12 months</li>
                  <li>• Transferable</li>
                  <li>• All therapists accepted</li>
                </ul>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600 mb-2">£100</p>
                  <button className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg font-medium transition-colors duration-200">
                    Purchase
                  </button>
                </div>
              </div>

              <div className="bg-white border border-blue-300 rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow duration-200 relative">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">Most Popular</span>
                </div>
                <div className="text-center mb-4">
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl font-bold text-blue-600">3</span>
                  </div>
                  <h3 className="text-xl font-semibold text-slate-800">Three Sessions</h3>
                </div>
                <ul className="space-y-2 text-gray-700 mb-4">
                  <li>• Three 50-minute sessions</li>
                  <li>• Valid for 12 months</li>
                  <li>• 10% discount included</li>
                  <li>• Flexible scheduling</li>
                </ul>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600 mb-2">£270</p>
                  <p className="text-sm text-gray-500 line-through">£300</p>
                  <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors duration-200">
                    Purchase
                  </button>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow duration-200">
                <div className="text-center mb-4">
                  <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl font-bold text-purple-600">∞</span>
                  </div>
                  <h3 className="text-xl font-semibold text-slate-800">Custom Amount</h3>
                </div>
                <ul className="space-y-2 text-gray-700 mb-4">
                  <li>• Choose any amount</li>
                  <li>• Minimum £50</li>
                  <li>• Valid for 12 months</li>
                  <li>• Use for any service</li>
                </ul>
                <div className="text-center">
                  <p className="text-lg text-purple-600 mb-2">£50 - £500+</p>
                  <button className="w-full bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-lg font-medium transition-colors duration-200">
                    Purchase
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* How It Works */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-slate-800 mb-6">How It Works</h2>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-lg font-bold text-blue-600">1</span>
                </div>
                <h3 className="font-semibold text-slate-800 mb-2">Choose Voucher</h3>
                <p className="text-sm text-gray-600">Select the voucher type and amount</p>
              </div>
              <div className="text-center">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-lg font-bold text-blue-600">2</span>
                </div>
                <h3 className="font-semibold text-slate-800 mb-2">Personalize</h3>
                <p className="text-sm text-gray-600">Add a personal message and recipient details</p>
              </div>
              <div className="text-center">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-lg font-bold text-blue-600">3</span>
                </div>
                <h3 className="font-semibold text-slate-800 mb-2">Purchase</h3>
                <p className="text-sm text-gray-600">Complete secure payment</p>
              </div>
              <div className="text-center">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-lg font-bold text-blue-600">4</span>
                </div>
                <h3 className="font-semibold text-slate-800 mb-2">Deliver</h3>
                <p className="text-sm text-gray-600">Digital voucher sent via email</p>
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-slate-800 mb-6">Voucher Features</h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">Flexibility</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Valid for any of our services</li>
                    <li>• Can be used with any therapist</li>
                    <li>• Flexible appointment scheduling</li>
                    <li>• Partial redemption allowed</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">Convenience</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Instant digital delivery</li>
                    <li>• 12-month validity period</li>
                    <li>• Easy online redemption</li>
                    <li>• Transferable to others</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Contact Information */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-slate-800 mb-6">Need Help?</h2>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <p className="text-gray-700 mb-4">
                If you have questions about gift vouchers or need assistance with your purchase, we're here to help.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-blue-600" />
                  <span className="text-gray-700">0333 800 3006</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-600" />
                  <span className="text-gray-700">info@healthcity.org.uk</span>
                </div>
              </div>
            </div>
          </section>

          {/* Terms */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-3">Terms & Conditions</h3>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Gift vouchers are valid for 12 months from date of purchase</li>
              <li>• Vouchers are non-refundable but are transferable</li>
              <li>• Appointment cancellations must be made 24 hours in advance</li>
              <li>• Vouchers cannot be exchanged for cash</li>
              <li>• Lost vouchers can be replaced with proof of purchase</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiftVouchersPage;